import React, { useEffect, useState } from "react";
import { Box, Dialog, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SaveOutlined, TuneOutlined } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import CustomTextField from "../../common/CustomTextField";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomDatePicker from "../../common/CustomDatePicker";
import { FieldLabel } from "../../../utils/Utility";
import { useModalStyles } from "../../common/useModalStyles";
import {
  ALIGNMENT_OPTIONS,
  IsrRecord,
  SCOPE_OPTIONS,
  SalesConfigFormValues,
  SalesConfigRecord,
  validateOptionalCompPercent,
} from "./isrTypes";

interface SalesConfigPopupProps {
  open: boolean;
  isrList: IsrRecord[];
  salesConfig?: SalesConfigRecord | null;
  onClose: () => void;
  onSubmitForm: (data: SalesConfigFormValues) => void;
}

const emptyValues: SalesConfigFormValues = {
  isrId: "",
  invoiceYear: "",
  effectiveFrom: null,
  effectiveTo: null,
  alignment: "",
  scope: "All Invoices",
  rateType: "STANDARD",
  standardCompPercent: "",
};

const SalesConfigPopup: React.FC<SalesConfigPopupProps> = ({
  open,
  isrList,
  salesConfig,
  onClose,
  onSubmitForm,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    dropdownWrapSx,
    datepickerWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const { control, handleSubmit, reset, formState: { errors } } = useForm<SalesConfigFormValues>({
    defaultValues: emptyValues,
    shouldUnregister: false,
  });

  const [isEdit, setIsEdit] = useState(false);
  const isrOptions = isrList.map((isr) => ({ label: isr.fullName, value: String(isr.isrId) }));

  useEffect(() => {
    if (!open) {
      return;
    }
    setIsEdit(!!salesConfig);
    if (salesConfig) {
      reset({
        isrId: String(salesConfig.isrId || ""),
        invoiceYear: salesConfig.invoiceYear,
        effectiveFrom: salesConfig.effectiveFrom ? dayjs(salesConfig.effectiveFrom) : null,
        effectiveTo: salesConfig.effectiveTo ? dayjs(salesConfig.effectiveTo) : null,
        alignment: salesConfig.alignment,
        scope: salesConfig.scope || "All Invoices",
        rateType: salesConfig.rateType === "TARGET_BASED" ? "TARGET_BASED" : "STANDARD",
        standardCompPercent:
          salesConfig.rateType === "STANDARD" ? salesConfig.standardCompPercent : "",
      });
      return;
    }
    reset(emptyValues);
  }, [open, salesConfig, reset]);

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmitForm)}
        sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <TuneOutlined sx={{ fontSize: 20, color: tokens.accent }} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              {isEdit ? "Edit Sales Config" : "Add Sales Config"}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              Set ISR commission rules by invoice year, alignment, and scope.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FieldLabel label="ISR Name" required tokens={tokens} />
              <Box sx={dropdownWrapSx(!!errors.isrId)}>
                <CustomDropdownField
                  name="isrId"
                  control={control}
                  options={isrOptions}
                  label=""
                  size="small"
                  rules={{ required: "ISR Name is required." }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Invoice Year" required tokens={tokens} />
              <Box sx={inputFieldSx(!!errors.invoiceYear)}>
                <CustomTextField
                  control={control}
                  controllerName="invoiceYear"
                  label=""
                  size="small"
                  rules={{
                    required: "Invoice Year is required.",
                    pattern: { value: /^\d{4}$/, message: "Enter a valid year." },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Effective From" required tokens={tokens} />
              <Box sx={datepickerWrapSx(!!errors.effectiveFrom)}>
                <CustomDatePicker
                  control={control}
                  controllerName="effectiveFrom"
                  label=""
                  size="small"
                  rules={{ required: "Effective From is required." }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Effective To" tokens={tokens} />
              <Box sx={datepickerWrapSx(!!errors.effectiveTo)}>
                <CustomDatePicker
                  control={control}
                  controllerName="effectiveTo"
                  label=""
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Alignment" required tokens={tokens} />
              <Box sx={dropdownWrapSx(!!errors.alignment)}>
                <CustomDropdownField
                  name="alignment"
                  control={control}
                  options={ALIGNMENT_OPTIONS}
                  label=""
                  size="small"
                  rules={{ required: "Alignment is required." }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Scope" required tokens={tokens} />
              <Box sx={dropdownWrapSx(!!errors.scope)}>
                <CustomDropdownField
                  name="scope"
                  control={control}
                  options={SCOPE_OPTIONS}
                  label=""
                  size="small"
                  rules={{ required: "Scope is required." }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Standard Comp %" tokens={tokens} />
              <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary, mb: 0.75 }}>
                Enter a value for standard rate. Leave blank to use target-based rates (configure from the row Action).
              </Typography>
              <Box sx={inputFieldSx(!!errors.standardCompPercent)}>
                <CustomTextField
                  control={control}
                  controllerName="standardCompPercent"
                  label=""
                  size="small"
                  rules={{
                    validate: (value: any) => validateOptionalCompPercent(value, "Standard Comp %"),
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexDirection: { xs: "column", sm: "row" } }}>
            <Box
              component="button"
              type="submit"
              sx={{
                ...primaryBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "none",
                cursor: "pointer",
                color: "#fff",
              }}
            >
              <SaveOutlined sx={{ fontSize: "1.1rem" }} />
              {isEdit ? "Update Config" : "Save Config"}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SalesConfigPopup;
